import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Container, Heading, Flex, HStack, VStack, Text, Badge, Button, Select, Input,
    InputGroup, InputLeftElement, SimpleGrid, Spinner, Table, Thead, Tbody, Tr, Th, Td,
    TableContainer, Tooltip, useToast, useBreakpointValue, Drawer, DrawerOverlay, DrawerContent,
    DrawerHeader, DrawerBody, DrawerCloseButton, Divider, IconButton, Tag, Wrap, WrapItem,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Alert, AlertIcon, useClipboard, Tabs, TabList, Tab, TabPanels, TabPanel,
} from '@chakra-ui/react';
import {
    FaSearch, FaSyncAlt, FaBoxOpen, FaLock, FaClock, FaHistory, FaUnlock, FaChevronLeft, FaChevronRight,
    FaLink, FaCopy,
} from 'react-icons/fa';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useSearchParams } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';
import FiltroMultiplo from '../components/FiltroMultiplo';
import ProdutoImagem from '../components/ProdutoImagem';
import { useConfirm } from '../components/ConfirmDialog';
import {
    getProdutosBloqueados, getResumoProdutosBloqueados, getHistoricoBloqueio, setBloqueioProduto,
    criarConsultaEstoque,
} from '../services/api';
import ConsultasEstoqueTab from './ConsultasEstoqueTab';
import ConfigConsultaTab from './ConfigConsultaTab';

dayjs.locale('pt-br');

const CORES_CAUSA = {
    furo_fornecedor: 'orange',
    conferencia_divergente: 'purple',
    devolucao_divergente: 'pink',
    lista_massa: 'blue',
    manual: 'teal',
    planilha: 'cyan',
    script_pontual: 'gray',
    fornecedor_descontinuado: 'gray',
    sem_dimensao: 'yellow',
    desconhecido: 'gray',
};

const ORDENACOES = [
    { valor: 'recentes', rotulo: 'Bloqueados mais recentes' },
    { valor: 'antigos', rotulo: 'Bloqueados há mais tempo' },
    { valor: 'mais_vendidos', rotulo: 'Mais vendidos (12 meses)' },
    { valor: 'menos_vendidos', rotulo: 'Menos vendidos (12 meses)' },
    { valor: 'maior_estoque', rotulo: 'Maior estoque no fornecedor' },
    { valor: 'nome', rotulo: 'Nome (A-Z)' },
];

const POR_PAGINA = [25, 50, 100, 200];

const brl = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataHora = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-');
const data = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '-');

// "781 dias" não diz nada de imediato; acima de dois meses o texto vira meses.
const tempoBloqueado = (dias) => {
    if (dias === null || dias === undefined) return null;
    if (dias === 0) return 'hoje';
    if (dias === 1) return 'há 1 dia';
    if (dias < 60) return `há ${dias} dias`;
    const meses = Math.floor(dias / 30);
    if (meses < 24) return `há ${meses} meses`;
    return `há ${Math.floor(meses / 12)} anos`;
};

const corDoTempo = (dias) => {
    if (dias === null || dias === undefined) return 'gray.500';
    if (dias >= 180) return 'red.500';
    if (dias >= 60) return 'orange.500';
    return 'gray.600';
};

// A origem guarda o id interno da venda; quem atende procura pelo número do
// marketplace, então os dois aparecem.
function OrigemVenda({ origem, venda }) {
    if (!venda) {
        return origem ? <Text fontSize="xs" color="gray.500" noOfLines={1}>{origem}</Text> : null;
    }
    return (
        <Text fontSize="xs" color="gray.500" noOfLines={1}>
            venda #{venda.id}
            {venda.id_origem ? ` · ${venda.id_origem}` : ''}
        </Text>
    );
}

function KpiCard({ icone, titulo, valor, cor, ajuda }) {
    return (
        <Tooltip label={ajuda} placement="top" hasArrow openDelay={300}>
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} boxShadow="sm">
                <HStack spacing={3} align="center">
                    <Flex w="38px" h="38px" borderRadius="md" bg={`${cor}.50`} color={`${cor}.500`} align="center" justify="center" flexShrink={0}>
                        {icone}
                    </Flex>
                    <Box minW={0}>
                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" noOfLines={1}>{titulo}</Text>
                        <Text fontSize="2xl" fontWeight="bold" lineHeight="1.1">{valor}</Text>
                    </Box>
                </HStack>
            </Box>
        </Tooltip>
    );
}

function BadgeCausa({ causa, rotulo }) {
    return (
        <Badge colorScheme={CORES_CAUSA[causa] || 'gray'} borderRadius="full" px={2} py={0.5} fontSize="xs" whiteSpace="nowrap">
            {rotulo}
        </Badge>
    );
}

// O link e o produto desta etapa: nao existe envio ainda, entao quem gera copia
// e abre na mao para validar a tela do fornecedor.
function ModalConsulta({ consulta, onClose }) {
    const { hasCopied, onCopy } = useClipboard(consulta?.url || '');

    return (
        <Modal isOpen={!!consulta} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Consulta gerada</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontSize="sm" color="gray.600">
                        {consulta?.itens} produto(s) bloqueado(s) por furo entraram nesta consulta.
                    </Text>

                    {consulta?.ensaio && (
                        <Alert status="warning" mt={3} borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="sm">
                                Consulta de teste: as respostas ficam gravadas, mas nenhum produto é alterado.
                            </Text>
                        </Alert>
                    )}

                    <Text fontSize="xs" color="gray.500" mt={4} mb={1}>Link do fornecedor</Text>
                    <Flex gap={2}>
                        <Input size="sm" value={consulta?.url || ''} isReadOnly onFocus={(e) => e.target.select()} />
                        <Button size="sm" leftIcon={<FaCopy />} onClick={onCopy} flexShrink={0}>
                            {hasCopied ? 'Copiado' : 'Copiar'}
                        </Button>
                    </Flex>

                    <Text fontSize="xs" color="gray.500" mt={3}>
                        Nenhuma mensagem foi enviada. O link vale por 15 dias.
                    </Text>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={2} onClick={onClose}>Fechar</Button>
                    <Button
                        colorScheme="orange" bg="#F68633" _hover={{ bg: '#e0762a' }}
                        onClick={() => window.open(consulta?.url, '_blank', 'noopener')}
                    >
                        Abrir a tela do fornecedor
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default function ProdutosBloqueados() {
    const toast = useToast();
    const { confirm, ConfirmPortal } = useConfirm();
    const isMobile = useBreakpointValue({ base: true, lg: false });

    // A aba fica na URL para o link poder ser mandado ja na aba certa.
    const [params, setParams] = useSearchParams();
    const ABAS = ['', 'consultas', 'config'];
    const aba = Math.max(0, ABAS.indexOf(params.get('aba') || ''));
    const trocarAba = (indice) => {
        const novo = new URLSearchParams(params);
        if (ABAS[indice]) novo.set('aba', ABAS[indice]); else novo.delete('aba');
        setParams(novo, { replace: true });
    };

    const [carregando, setCarregando] = useState(true);
    const [itens, setItens] = useState([]);
    const [meta, setMeta] = useState({ pagina: 1, total: 0, ultima_pagina: 1 });
    const [resumo, setResumo] = useState({ totais: {}, por_causa: [], fornecedores: [] });

    const [busca, setBusca] = useState('');
    const [buscaAplicada, setBuscaAplicada] = useState('');
    const [causas, setCausas] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [tipo, setTipo] = useState('');
    const [ordenacao, setOrdenacao] = useState('recentes');
    const [porPagina, setPorPagina] = useState(50);
    const [pagina, setPagina] = useState(1);

    const [consulta, setConsulta] = useState(null);
    const [gerandoConsulta, setGerandoConsulta] = useState(false);

    const [historico, setHistorico] = useState(null);
    const [produtoHistorico, setProdutoHistorico] = useState(null);
    const [carregandoHistorico, setCarregandoHistorico] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const resposta = await getProdutosBloqueados({
                busca: buscaAplicada || undefined,
                causa: causas.length ? causas.join(',') : undefined,
                fornecedor_id: fornecedores.length ? fornecedores.join(',') : undefined,
                tipo: tipo || undefined,
                ordenacao,
                por_pagina: porPagina,
                pagina,
            });
            setItens(resposta?.data || []);
            setMeta(resposta?.meta || { pagina: 1, total: 0, ultima_pagina: 1 });
        } catch {
            toast({ title: 'Não foi possível carregar os produtos bloqueados.', status: 'error', isClosable: true });
        } finally {
            setCarregando(false);
        }
    }, [buscaAplicada, causas, fornecedores, tipo, ordenacao, porPagina, pagina, toast]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        (async () => {
            try {
                const r = await getResumoProdutosBloqueados();
                if (r) setResumo(r);
            } catch {
                // o resumo é complementar: a lista continua utilizável sem ele
            }
        })();
    }, []);

    const aplicarBusca = (e) => {
        e?.preventDefault?.();
        setPagina(1);
        setBuscaAplicada(busca.trim());
    };

    const trocarFiltro = (setter) => (e) => {
        setPagina(1);
        setter(e.target.value);
    };

    const trocarMulti = (setter) => (valores) => {
        setPagina(1);
        setter(valores || []);
    };

    // So faz sentido com UM fornecedor escolhido: a consulta vai para ele, e
    // cada fornecedor recebe a sua.
    const gerarConsulta = async () => {
        setGerandoConsulta(true);
        const r = await criarConsultaEstoque({
            fornecedor_id: Number(fornecedores[0]),
        });
        setGerandoConsulta(false);

        if (r?.erro) {
            toast({ title: r.erro, status: 'error', isClosable: true });
            return;
        }
        setConsulta(r);
    };

    const abrirHistorico = async (produto) => {
        setProdutoHistorico(produto);
        setCarregandoHistorico(true);
        try {
            const r = await getHistoricoBloqueio(produto.id);
            setHistorico(r?.data || []);
        } catch {
            setHistorico([]);
            toast({ title: 'Não foi possível carregar o histórico.', status: 'error', isClosable: true });
        } finally {
            setCarregandoHistorico(false);
        }
    };

    const desbloquear = async (produto) => {
        const ok = await confirm({
            title: 'Desbloquear produto',
            body: `O produto #${produto.id} volta a ser anunciado se houver estoque no fornecedor. Confirma?`,
            confirmText: 'Desbloquear',
            cancelText: 'Cancelar',
            confirmColor: 'green',
        });
        if (!ok) return;

        const resposta = await setBloqueioProduto(produto.id, 0);
        if (resposta === false) {
            toast({ title: 'Erro ao desbloquear o produto.', status: 'error', isClosable: true });
            return;
        }
        toast({ title: 'Produto desbloqueado.', status: 'success', isClosable: true });
        carregar();
    };

    const opcoesCausa = useMemo(() => (
        (resumo.por_causa || [])
            .filter((c) => !!c.causa)
            .map((c) => ({ valor: c.causa, rotulo: `${c.rotulo} (${c.total})` }))
    ), [resumo.por_causa]);

    const opcoesFornecedor = useMemo(() => (
        (resumo.fornecedores || []).map((f) => ({ valor: String(f.fornecedor_id), rotulo: `${f.nome} (${f.total})` }))
    ), [resumo.fornecedores]);

    const totais = resumo.totais || {};
    const temFiltro = !!(buscaAplicada || causas.length || fornecedores.length || tipo);

    const limparFiltros = () => {
        setBusca(''); setBuscaAplicada(''); setCausas([]); setFornecedores([]); setTipo(''); setOrdenacao('recentes'); setPagina(1);
    };

    const paginacao = useMemo(() => (
        <Flex justify="space-between" align="center" gap={3} mt={4} wrap="wrap">
            <Text fontSize="sm" color="gray.600">
                {meta.total > 0
                    ? `Página ${meta.pagina} de ${meta.ultima_pagina} · ${meta.total.toLocaleString('pt-BR')} produto(s)`
                    : 'Nenhum produto'}
            </Text>
            <HStack>
                <IconButton
                    aria-label="Página anterior" icon={<FaChevronLeft />} size="sm" variant="outline"
                    isDisabled={meta.pagina <= 1 || carregando}
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                />
                <IconButton
                    aria-label="Próxima página" icon={<FaChevronRight />} size="sm" variant="outline"
                    isDisabled={meta.pagina >= meta.ultima_pagina || carregando}
                    onClick={() => setPagina((p) => p + 1)}
                />
            </HStack>
        </Flex>
    ), [meta, carregando]);

    return (
        <Box bg="gray.50" minH="100vh">
            <ScrollToTop />
            <Container maxW="1280px" py={6}>
                <Flex justify="space-between" align="center" mb={1} wrap="wrap" gap={3}>
                    <Heading size="lg" color="#F68633" fontWeight="extrabold">Produtos Bloqueados</Heading>
                    {aba === 0 && (
                        <Button leftIcon={<FaSyncAlt />} size="sm" variant="outline" onClick={carregar} isLoading={carregando}>
                            Atualizar
                        </Button>
                    )}
                </Flex>
                <Text color="gray.600" fontSize="sm" mb={4}>
                    {{
                        0: 'Por que cada produto saiu do ar, desde quando e quanto ele vendia.',
                        1: 'O que já foi perguntado a cada fornecedor e o que ele respondeu.',
                        2: 'Quando a pergunta sai, para quantos fornecedores e quais peças entram na lista.',
                    }[aba]}
                </Text>

                <Tabs
                    index={aba} onChange={trocarAba} colorScheme="orange" variant="line" isLazy
                    mb={5}
                >
                    <TabList mb={5} overflowX="auto" overflowY="hidden">
                        <Tab fontWeight="semibold" whiteSpace="nowrap">Produtos bloqueados</Tab>
                        <Tab fontWeight="semibold" whiteSpace="nowrap">Consultas ao fornecedor</Tab>
                        <Tab fontWeight="semibold" whiteSpace="nowrap">Configuração</Tab>
                    </TabList>

                    <TabPanels>
                    <TabPanel p={0}>

                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} mb={5}>
                    <KpiCard
                        icone={<FaLock />} cor="red" titulo="Bloqueados"
                        valor={(totais.bloqueados || 0).toLocaleString('pt-BR')}
                        ajuda="Produtos com bloqueio permanente ou temporário"
                    />
                    <KpiCard
                        icone={<FaBoxOpen />} cor="orange" titulo="Com estoque no fornecedor"
                        valor={(totais.com_estoque || 0).toLocaleString('pt-BR')}
                        ajuda="Bloqueados que o fornecedor diz ter em estoque: é aqui que mora a venda parada"
                    />
                    <KpiCard
                        icone={<FaClock />} cor="yellow" titulo="Bloqueio temporário"
                        valor={(totais.temporarios || 0).toLocaleString('pt-BR')}
                        ajuda="Voltam sozinhos quando o prazo vence"
                    />
                </SimpleGrid>

                <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} mb={4} boxShadow="sm">
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                        <form onSubmit={aplicarBusca}>
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none" color="gray.400"><FaSearch /></InputLeftElement>
                                <Input
                                    placeholder="SKU, nome ou código"
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                    onBlur={aplicarBusca}
                                    borderRadius="md"
                                />
                            </InputGroup>
                        </form>

                        <FiltroMultiplo
                            size="sm"
                            largura="100%"
                            rotuloTodos="Todas as causas"
                            opcoes={opcoesCausa}
                            selecionados={causas}
                            onChange={trocarMulti(setCausas)}
                        />

                        <FiltroMultiplo
                            size="sm"
                            largura="100%"
                            rotuloTodos="Todos os fornecedores"
                            opcoes={opcoesFornecedor}
                            selecionados={fornecedores}
                            onChange={trocarMulti(setFornecedores)}
                        />

                        <Select size="sm" value={tipo} onChange={trocarFiltro(setTipo)} borderRadius="md">
                            <option value="">Permanente e temporário</option>
                            <option value="1">Só permanente</option>
                            <option value="2">Só temporário</option>
                        </Select>
                    </SimpleGrid>

                    <Flex mt={3} gap={3} align="center" wrap="wrap" justify="space-between">
                        <HStack spacing={2} flex="1" minW={{ base: '100%', md: '320px' }}>
                            <Text fontSize="sm" color="gray.600" flexShrink={0}>Ordenar por</Text>
                            <Select size="sm" value={ordenacao} onChange={trocarFiltro(setOrdenacao)} borderRadius="md" maxW="280px">
                                {ORDENACOES.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                            </Select>
                        </HStack>

                        <HStack spacing={2}>
                            <Text fontSize="sm" color="gray.600" flexShrink={0}>Por página</Text>
                            <Select
                                size="sm" value={porPagina} borderRadius="md" maxW="100px"
                                onChange={(e) => { setPagina(1); setPorPagina(Number(e.target.value)); }}
                            >
                                {POR_PAGINA.map((n) => <option key={n} value={n}>{n}</option>)}
                            </Select>
                            {fornecedores.length === 1 && (
                                <Tooltip label="Gera o link de consulta de estoque para este fornecedor" hasArrow>
                                    <Button
                                        size="sm" variant="outline" colorScheme="orange" leftIcon={<FaLink />}
                                        onClick={gerarConsulta} isLoading={gerandoConsulta} flexShrink={0}
                                    >
                                        Gerar consulta
                                    </Button>
                                </Tooltip>
                            )}
                            {temFiltro && (
                                <Button size="sm" variant="ghost" onClick={limparFiltros} flexShrink={0}>Limpar filtros</Button>
                            )}
                        </HStack>
                    </Flex>
                </Box>

                {carregando ? (
                    <Flex justify="center" align="center" py={16}><Spinner size="lg" color="#F68633" thickness="3px" /></Flex>
                ) : itens.length === 0 ? (
                    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" py={16} textAlign="center">
                        <Box color="gray.300" fontSize="40px" mb={3}><FaBoxOpen style={{ margin: '0 auto' }} /></Box>
                        <Text color="gray.600" fontWeight="medium">Nenhum produto bloqueado encontrado</Text>
                        <Text color="gray.500" fontSize="sm" mt={1}>
                            {temFiltro ? 'Tente ajustar os filtros.' : 'Nada bloqueado no momento.'}
                        </Text>
                    </Box>
                ) : isMobile ? (
                    <VStack spacing={3} align="stretch">
                        {itens.map((p) => (
                            <Box key={p.id} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} boxShadow="sm">
                                <Flex gap={3} mb={2}>
                                    <Box flexShrink={0} borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
                                        <ProdutoImagem produto={{ produto_id: p.id }} origem={null} width="56px" height="56px" />
                                    </Box>
                                    <Box minW={0} flex={1}>
                                        <Text fontSize="xs" color="gray.500">SKU {p.id}</Text>
                                        <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>{p.nome}</Text>
                                        <Wrap spacing={1} mt={1}>
                                            <WrapItem><BadgeCausa causa={p.causa} rotulo={p.causa_rotulo} /></WrapItem>
                                            {(p.divergencia?.itens || []).map((d) => (
                                                <WrapItem key={d}>
                                                    <Tag size="sm" variant="subtle" colorScheme="gray" fontSize="10px">{d}</Tag>
                                                </WrapItem>
                                            ))}
                                            {p.tipo === 2 && (
                                                <WrapItem><Badge colorScheme="yellow" fontSize="xs">Temporário</Badge></WrapItem>
                                            )}
                                        </Wrap>
                                    </Box>
                                </Flex>

                                <Text
                                    fontSize="sm" bg="gray.50" borderRadius="md" px={3} py={2} mb={2}
                                    color={p.motivo || p.divergencia?.observacao ? 'gray.700' : 'gray.400'}
                                    fontStyle={p.motivo || p.divergencia?.observacao ? 'normal' : 'italic'}
                                >
                                    {p.motivo || p.divergencia?.observacao || 'Sem motivo registrado'}
                                </Text>
                                <Box mb={2}><OrigemVenda origem={p.origem} venda={p.origem_venda} /></Box>

                                <SimpleGrid columns={2} spacing={2} fontSize="xs" color="gray.600" mb={3}>
                                    <Text>Bloqueado <b style={{ color: corDoTempo(p.dias_bloqueado) }}>{tempoBloqueado(p.dias_bloqueado)}</b></Text>
                                    <Text>Estoque forn.: <b>{p.estoque_fornecedor}</b></Text>
                                    <Text>Por <b>{p.bloqueado_por || 'Sistema'}</b></Text>
                                    <Text>12m: <b>{p.vendas_12m.pedidos}</b> pedidos</Text>
                                </SimpleGrid>

                                <HStack>
                                    <Button size="xs" leftIcon={<FaHistory />} variant="outline" onClick={() => abrirHistorico(p)} flex={1}>
                                        Ver detalhes
                                    </Button>
                                    <Button size="xs" leftIcon={<FaUnlock />} colorScheme="green" variant="outline" onClick={() => desbloquear(p)} flex={1}>
                                        Desbloquear
                                    </Button>
                                </HStack>
                            </Box>
                        ))}
                        {paginacao}
                    </VStack>
                ) : (
                    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" boxShadow="sm" overflow="hidden">
                        <TableContainer>
                            <Table size="sm" variant="simple">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th>Produto</Th>
                                        <Th>Causa</Th>
                                        <Th>Motivo e origem</Th>
                                        <Th>Bloqueado</Th>
                                        <Th isNumeric>Estoque</Th>
                                        <Th isNumeric>Vendas 12m</Th>
                                        <Th></Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {itens.map((p) => (
                                        <Tr
                                            key={p.id}
                                            _hover={{ bg: 'gray.50' }}
                                            cursor="pointer"
                                            onClick={() => abrirHistorico(p)}
                                            title="Abrir detalhes do bloqueio"
                                        >
                                            <Td maxW="320px">
                                                <HStack spacing={3} align="start">
                                                    <Box flexShrink={0} borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
                                                        <ProdutoImagem produto={{ produto_id: p.id }} origem={null} width="48px" height="48px" />
                                                    </Box>
                                                    <Box minW={0}>
                                                        <Text fontSize="xs" color="gray.500">SKU {p.id}</Text>
                                                        <Text fontSize="sm" fontWeight="medium" noOfLines={2}>{p.nome}</Text>
                                                        {p.codigo_fornecedor && (
                                                            <Text fontSize="xs" color="gray.400" noOfLines={1}>Cód. {p.codigo_fornecedor}</Text>
                                                        )}
                                                    </Box>
                                                </HStack>
                                            </Td>
                                            <Td>
                                                <VStack align="start" spacing={1}>
                                                    <BadgeCausa causa={p.causa} rotulo={p.causa_rotulo} />
                                                    {!!p.divergencia?.itens?.length && (
                                                        <Wrap spacing={1}>
                                                            {p.divergencia.itens.map((d) => (
                                                                <WrapItem key={d}>
                                                                    <Tag size="sm" variant="subtle" colorScheme="gray" fontSize="10px">{d}</Tag>
                                                                </WrapItem>
                                                            ))}
                                                        </Wrap>
                                                    )}
                                                    {p.tipo === 2 && <Badge colorScheme="yellow" fontSize="xs">Temporário</Badge>}
                                                </VStack>
                                            </Td>
                                            <Td maxW="300px">
                                                {p.motivo || p.divergencia?.observacao ? (
                                                    <Text fontSize="sm" color="gray.700" noOfLines={2}>
                                                        {p.motivo || p.divergencia.observacao}
                                                    </Text>
                                                ) : (
                                                    <Text fontSize="sm" color="gray.400" fontStyle="italic">Sem motivo registrado</Text>
                                                )}
                                                <OrigemVenda origem={p.origem} venda={p.origem_venda} />
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm">{data(p.bloqueio_em)}</Text>
                                                <Text fontSize="xs" color={corDoTempo(p.dias_bloqueado)} fontWeight="medium">
                                                    {tempoBloqueado(p.dias_bloqueado)}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>{p.bloqueado_por || 'Sistema'}</Text>
                                            </Td>
                                            <Td isNumeric>
                                                <Text fontSize="sm" fontWeight={p.estoque_fornecedor > 0 ? 'bold' : 'normal'} color={p.estoque_fornecedor > 0 ? 'orange.500' : 'gray.400'}>
                                                    {p.estoque_fornecedor}
                                                </Text>
                                            </Td>
                                            <Td isNumeric>
                                                {p.vendas_12m.pedidos > 0 ? (
                                                    <>
                                                        <Text fontSize="sm" fontWeight="medium">{brl(p.vendas_12m.receita)}</Text>
                                                        <Text fontSize="xs" color="gray.500">{p.vendas_12m.pedidos} pedido(s)</Text>
                                                    </>
                                                ) : (
                                                    <Text fontSize="sm" color="gray.400">-</Text>
                                                )}
                                            </Td>
                                            <Td>
                                                <HStack spacing={1} justify="end">
                                                    <Tooltip label="Ver tudo sobre este bloqueio" hasArrow>
                                                        <IconButton aria-label="Detalhes" icon={<FaHistory />} size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); abrirHistorico(p); }} />
                                                    </Tooltip>
                                                    <Tooltip label="Desbloquear" hasArrow>
                                                        <IconButton aria-label="Desbloquear" icon={<FaUnlock />} size="xs" variant="ghost" colorScheme="green" onClick={(e) => { e.stopPropagation(); desbloquear(p); }} />
                                                    </Tooltip>
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                        <Box px={4} pb={3}>{paginacao}</Box>
                    </Box>
                )}

                    </TabPanel>

                    <TabPanel p={0}>
                        <ConsultasEstoqueTab fornecedores={resumo.fornecedores} />
                    </TabPanel>

                    <TabPanel p={0}>
                        <ConfigConsultaTab />
                    </TabPanel>
                    </TabPanels>
                </Tabs>
            </Container>

            <ModalConsulta consulta={consulta} onClose={() => setConsulta(null)} />

            <Drawer isOpen={!!produtoHistorico} placement="right" size="lg" onClose={() => { setProdutoHistorico(null); setHistorico(null); }}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px">
                        <Text fontSize="sm" color="gray.500">SKU {produtoHistorico?.id}</Text>
                        <Text fontSize="md">{produtoHistorico?.nome}</Text>
                    </DrawerHeader>
                    <DrawerBody py={4}>
                        {/* O texto aqui nunca e cortado: e para isso que a gaveta existe. */}
                        {produtoHistorico && (
                            <VStack align="stretch" spacing={4} mb={6}>
                                <Box borderWidth="1px" borderColor="red.100" bg="red.50" borderRadius="lg" p={4}>
                                    <Wrap spacing={2} mb={2}>
                                        <WrapItem><BadgeCausa causa={produtoHistorico.causa} rotulo={produtoHistorico.causa_rotulo} /></WrapItem>
                                        {(produtoHistorico.divergencia?.itens || []).map((d) => (
                                            <WrapItem key={d}><Tag size="sm" colorScheme="gray">{d}</Tag></WrapItem>
                                        ))}
                                        {produtoHistorico.tipo === 2 && (
                                            <WrapItem><Badge colorScheme="yellow">Temporário</Badge></WrapItem>
                                        )}
                                    </Wrap>

                                    <Text fontSize="sm" color="gray.800">
                                        {produtoHistorico.motivo || produtoHistorico.divergencia?.observacao || 'Sem motivo registrado.'}
                                    </Text>

                                    <VStack align="stretch" spacing={0.5} fontSize="xs" color="gray.600" mt={2}>
                                        {produtoHistorico.origem_venda ? (
                                            <Text>
                                                Venda #{produtoHistorico.origem_venda.id}
                                                {produtoHistorico.origem_venda.id_origem ? ` · ${produtoHistorico.origem_venda.id_origem}` : ''}
                                                {produtoHistorico.origem_venda.origem ? ` · ${produtoHistorico.origem_venda.origem}` : ''}
                                            </Text>
                                        ) : produtoHistorico.origem && <Text>Origem: {produtoHistorico.origem}</Text>}
                                        <Text>
                                            Bloqueado em {dataHora(produtoHistorico.bloqueio_em)} ({tempoBloqueado(produtoHistorico.dias_bloqueado)})
                                            {produtoHistorico.bloqueado_por ? ` por ${produtoHistorico.bloqueado_por}` : ''}
                                        </Text>
                                    </VStack>
                                </Box>

                                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
                                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase">Estoque fornecedor</Text>
                                        <Text fontSize="lg" fontWeight="bold" color={produtoHistorico.estoque_fornecedor > 0 ? 'orange.500' : 'gray.400'}>
                                            {produtoHistorico.estoque_fornecedor}
                                        </Text>
                                    </Box>
                                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase">Vendas 12 meses</Text>
                                        <Text fontSize="lg" fontWeight="bold">{brl(produtoHistorico.vendas_12m?.receita)}</Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {produtoHistorico.vendas_12m?.pedidos || 0} pedido(s)
                                            {produtoHistorico.vendas_12m?.ultima_venda ? ` · última em ${data(produtoHistorico.vendas_12m.ultima_venda)}` : ''}
                                        </Text>
                                    </Box>
                                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase">Preço</Text>
                                        <Text fontSize="lg" fontWeight="bold">{brl(produtoHistorico.valor_venda)}</Text>
                                        {produtoHistorico.codigo_fornecedor && (
                                            <Text fontSize="xs" color="gray.500">Cód. {produtoHistorico.codigo_fornecedor}</Text>
                                        )}
                                    </Box>
                                </SimpleGrid>

                                <Text fontSize="sm" fontWeight="bold" pt={2}>Histórico de bloqueios</Text>
                            </VStack>
                        )}

                        {carregandoHistorico ? (
                            <Flex justify="center" py={10}><Spinner color="#F68633" /></Flex>
                        ) : !historico?.length ? (
                            <Text color="gray.500" fontSize="sm">Sem histórico registrado para este produto.</Text>
                        ) : (
                            <VStack align="stretch" spacing={4}>
                                {historico.map((e) => (
                                    <Box key={e.id} borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                                        <Flex justify="space-between" align="start" gap={2} mb={2}>
                                            <BadgeCausa causa={e.causa} rotulo={e.causa_rotulo} />
                                            <Tag size="sm" colorScheme={e.desbloqueado_em ? 'gray' : 'red'}>
                                                {e.desbloqueado_em ? 'Encerrado' : 'Em vigor'}
                                            </Tag>
                                        </Flex>

                                        {e.motivo && <Text fontSize="sm" color="gray.700" mb={2}>{e.motivo}</Text>}

                                        <VStack align="stretch" spacing={0.5} fontSize="xs" color="gray.600">
                                            <Text>Bloqueado em {dataHora(e.bloqueado_em)} por {e.bloqueado_por || 'Sistema'}</Text>
                                            {e.origem && <Text>Origem: {e.origem}</Text>}
                                            {e.tipo === 2 && <Text>Bloqueio temporário</Text>}
                                        </VStack>

                                        {e.desbloqueado_em && (
                                            <>
                                                <Divider my={2} />
                                                <VStack align="stretch" spacing={0.5} fontSize="xs" color="gray.600">
                                                    <Text>Desbloqueado em {dataHora(e.desbloqueado_em)} por {e.desbloqueado_por || 'Sistema'}</Text>
                                                    {e.desbloqueio_causa && <Text>Motivo do desbloqueio: {e.desbloqueio_causa}</Text>}
                                                    {e.desbloqueio_motivo && <Text>{e.desbloqueio_motivo}</Text>}
                                                </VStack>
                                            </>
                                        )}
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <ConfirmPortal />
        </Box>
    );
}
